<template>
  <div>
    <div>
      <table :class="tableClass || $style.table">
        <thead>
          <tr>
            <th
              v-if="withSelection"
              style="width: 0"
              :class="[!disableSortSelected && hasSorting && $style.sortable]"
              @click.stop="!disableSortSelected && onSort('isSelected')"
            >
              <div class="gap-1">
                <span
                  v-if="hasSorting"
                  :data-i="
                    disableSortSelected ? undefined : sortIcon('isSelected')
                  "
                  :class="{
                    [$style.isDesc]: sortBy === 'isSelected' && isDescending,
                  }"
                  style="--offsetRight: 0"
                />
              </div>
            </th>
            <th
              v-for="header in headers"
              :key="header.value"
              :style="header.style"
              :class="[
                header.class,
                !header.disableSort && hasSorting && $style.sortable,
              ]"
              @click.stop="!header.disableSort && onSort(header.value)"
            >
              <div v-tooltip="header.tooltip" class="flex items-center gap-1">
                <i
                  v-if="header.icon"
                  :data-i="header.icon"
                  style="--offsetRight: 0"
                />
                {{ header.text }}
                <span
                  v-if="hasSorting"
                  :data-i="
                    header.disableSort ? undefined : sortIcon(header.value)
                  "
                  :class="{
                    [$style.isDesc]: sortBy === header.value && isDescending,
                  }"
                  style="--offsetRight: 0"
                />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in paginatedRows"
            :key="row.id"
            :data-row-id="row.id"
            :class="{
              [$style.selected]: row.isSelected,
              [$style.disabled]: row.isDisabled,
            }"
            @click.stop="
              !row.isDisabled && $emit('onSelected', !row.isSelected, row)
            "
          >
            <td v-if="withSelection">
              <div class="flex items-center gap-2">
                <input
                  :id="row.id"
                  type="checkbox"
                  data-testid="checkbox-row-select"
                  :checked="Boolean(row.isSelected)"
                  :disabled="row.isDisabled"
                  :indeterminate.prop="row.isSelected === null"
                  :class="$style.selectionCheckbox"
                />
              </div>
            </td>
            <td
              v-for="header in headers"
              :key="header.value"
              :style="header.style"
              :class="header.class"
            >
              <slot :name="header.value" :row="row" :value="row[header.value]">
                {{ row[header.value] }}
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <BasePagination
      v-if="props.itemsPerPage"
      :perPage="props.itemsPerPage"
      :totalPages="totalPages"
      :currentPage="currentPage"
      @pagechanged="onPageChanged"
    >
      <template #pageInfo>
        {{ paginationInfo }}
      </template>
    </BasePagination>
  </div>
</template>
<script lang="ts">
export type GenericTableHeader = {
  text: string;
  class?: string;
  style?: string;
  disableSort?: boolean;
  tooltip?: string;
  icon?: string;
};
</script>
<script setup lang="ts" generic="T extends {}, K extends keyof T">
import { watch, computed, ref } from 'vue';
import BasePagination from './BasePagination.vue';
import { useTranslation } from '@/composables/plugins';

const { t } = useTranslation();

// One collator for the whole sort: `localeCompare` with options builds one per comparison.
const collator = new Intl.Collator(undefined, { numeric: true });

const emit = defineEmits(['onSelected', 'onPageChanged', 'onSortChanged']);
const props = defineProps<{
  withSelection?: boolean;
  withSorting?: boolean;
  serverSorting?: boolean;
  tableClass?: string | string[];
  disableSortSelected?: boolean;
  initialSortBy?: K;
  initialIsDescending?: boolean;
  rows: (T & { id: string; isSelected?: boolean; isDisabled?: boolean })[];
  headers: (GenericTableHeader & { value: K })[];
  itemsPerPage?: number;
}>();

const sortBy = ref<K | 'isSelected'>();
const isDescending = ref(props.initialIsDescending);
const currentPage = ref(1);
const itemsPerPage = computed(() => props.itemsPerPage);

watch(
  () => props.rows.length,
  () => {
    currentPage.value = 1;
  }
);

watch(
  () => currentPage.value,
  () =>
    emit('onPageChanged', {
      currentPage: currentPage.value,
      totalPages: totalPages.value,
    })
);

watch(
  () => props.initialSortBy,
  (initialSortBy) => (sortBy.value = initialSortBy),
  { immediate: true }
);

watch(
  () => props.initialIsDescending,
  (initialIsDescending) => {
    if (props.serverSorting) isDescending.value = initialIsDescending;
  }
);

const sortedRows = computed(() => {
  if (!sortBy.value || props.serverSorting) return props.rows;

  return props.rows.slice().sort((a, b) => {
    const key = sortBy.value;
    const sortSgn = isDescending.value ? -1 : 1;
    if (!key) return 0;
    if (typeof a[key] === 'string' && typeof b[key] === 'string') {
      return sortSgn * collator.compare(a[key] as string, b[key] as string);
    }

    return sortSgn * (Number(a[key]) - Number(b[key]));
  });
});

const totalPages = computed(() => {
  if (!itemsPerPage.value) return 1;
  return Math.ceil(sortedRows.value.length / itemsPerPage.value);
});

const paginatedRows = computed(() => {
  if (!itemsPerPage.value) {
    return sortedRows.value;
  }
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = Math.min(start + itemsPerPage.value, sortedRows.value.length);
  return sortedRows.value.slice(start, end);
});

const paginationInfo = computed(() => {
  if (!itemsPerPage.value) {
    return '';
  }
  const total = sortedRows.value.length;
  if (total <= 1) {
    return '';
  }
  const from = (currentPage.value - 1) * itemsPerPage.value + 1;
  const to = Math.min(
    currentPage.value * itemsPerPage.value,
    sortedRows.value.length
  );
  return t('common:pagination.totalItemsPaginated', {
    from,
    to,
    total,
  });
});

const hasSorting = computed(() => props.withSorting && props.rows.length > 1);

function sortIcon(val: K | 'isSelected') {
  if (val !== sortBy.value) return 'swap_vert';
  return 'straight';
}

function onSort(val: K | 'isSelected') {
  if (!hasSorting.value) return;
  if (val === sortBy.value) {
    isDescending.value = !isDescending.value;
  }

  sortBy.value = val;
  emit('onSortChanged', {
    sortBy: val,
    isDescending: Boolean(isDescending.value),
  });
}

function onPageChanged(page: number) {
  currentPage.value = page;
}
</script>
<style module>
.table {
  --tableFontSize: 0.875rem;
  --borderColor: var(--neutral-200);
  --focusBackground: var(--blue-100);
  --theadBackground: var(--neutral-200);
  --tbodyBackground: transparent;
  --tbodyEvenBackground: var(--neutral-100);
  --primaryColor: var(--amber-600);
  --highlightBackground: var(--amber-300);

  --defaultPadding: 0.5rem 0.25rem;
  --thPadding: var(--defaultPadding);
  --tdPadding: var(--defaultPadding);
  --textAlign: left;

  width: 100%;
  font-size: var(--tableFontSize);
  border-collapse: collapse;
  text-align: var(--textAlign);

  & thead {
    background-color: var(--theadBackground);
  }

  & tbody {
    background-color: var(--tbodyBackground);
  }

  & tr:nth-child(even) {
    background-color: var(--tbodyEvenBackground);
  }

  & th {
    padding: var(--thPadding);
    padding-right: 0;
    font-weight: 500;

    &:not(:last-child) > div {
      padding-right: 0.25rem;
      border-right: 1px solid var(--borderColor);
      justify-content: var(--justifyContent, center);
    }
  }

  & td {
    padding: var(--tdPadding);
    border-bottom: 1px solid var(--borderColor);
  }

  & td,
  & th {
    &:first-child {
      padding-left: 0.75rem;
    }

    &:last-child {
      padding-right: 0.75rem;
    }
  }

  & tbody tr:focus-within:has(input) {
    background: var(--focusBackground);
  }

  & tbody tr:has(input.selectionCheckbox):hover,
  & tbody tr:has(input.selectionCheckbox:checked),
  & tbody tr.selected {
    background: var(--highlightBackground);
  }

  & tbody tr:has(input.selectionCheckbox:checked) {
    box-shadow: 2px 0 0 0 inset var(--primaryColor);
  }
}

.isDesc::before {
  transform: scaleY(-1);
}

.sortable {
  cursor: pointer;
}

.table tbody tr.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
